import React, { useEffect, useState, createContext, useContext } from 'react';
import { Product, CartItem, Order, User, PromoCode, AdminSettings } from '../types';
interface AdminAuth {
  isAuthenticated: boolean;
  username: string;
}
interface AppContextType {
  products: Product[];
  cart: CartItem[];
  orders: Order[];
  user: User | null;
  adminAuth: AdminAuth | null;
  blockedUsers: string[];
  promoCodes: PromoCode[];
  adminSettings: AdminSettings;
  addToCart: (product: Product, quantity?: number, selectedSize?: string, selectedColor?: string) => void;
  removeFromCart: (productId: string) => void;
  updateCartQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  addOrder: (order: Order) => void;
  updateOrderStatus: (orderId: string, status: Order['status']) => void;
  updateOrderDeliveryDate: (orderId: string, expectedDate: string, deliveryDate?: string) => void;
  login: (user: User) => void;
  logout: () => void;
  updateUser: (user: User) => void;
  addProduct: (product: Product) => void;
  editProduct: (id: string, product: Product) => void;
  deleteProduct: (id: string) => void;
  adminLogin: (username: string, password: string) => boolean;
  adminLogout: () => void;
  blockUser: (mobile: string) => void;
  activateUser: (mobile: string) => void;
  addPromoCode: (promo: PromoCode) => void;
  editPromoCode: (id: string, promo: PromoCode) => void;
  deletePromoCode: (id: string) => void;
  validatePromoCode: (code: string, productIds: string[]) => {
    valid: boolean;
    promo?: PromoCode;
    error?: string;
  };
  updateAdminSettings: (settings: Partial<AdminSettings>) => void;
}
const defaultSettings: AdminSettings = {
  accentColor: '#1E3A8A',
  animationsEnabled: true,
  deliveryCharge: 0,
  promoSystemEnabled: true,
  bannerText: 'Sky Brand Collection',
  bannerSubtext: 'Premium Clothing for Men, Women & Kids',
  maintenanceMode: false,
  whatsappNotifications: true
};
const AppContext = createContext<AppContextType | undefined>(undefined);
export const AppProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [adminAuth, setAdminAuth] = useState<AdminAuth | null>(null);
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [adminSettings, setAdminSettings] = useState<AdminSettings>(defaultSettings);
  // Load from local storage on mount
  useEffect(() => {
    const savedProducts = localStorage.getItem('skybrand_products');
    const savedCart = localStorage.getItem('skybrand_cart');
    const savedOrders = localStorage.getItem('skybrand_orders');
    const savedUser = localStorage.getItem('skybrand_user');
    const savedAdmin = localStorage.getItem('skybrand_admin');
    const savedBlockedUsers = localStorage.getItem('skybrand_blocked_users');
    const savedPromos = localStorage.getItem('skybrand_promos');
    const savedSettings = localStorage.getItem('skybrand_settings');
    if (savedProducts) setProducts(JSON.parse(savedProducts));
    if (savedCart) setCart(JSON.parse(savedCart));
    if (savedOrders) setOrders(JSON.parse(savedOrders));
    if (savedUser) setUser(JSON.parse(savedUser));
    if (savedAdmin) setAdminAuth(JSON.parse(savedAdmin));
    if (savedBlockedUsers) setBlockedUsers(JSON.parse(savedBlockedUsers));
    if (savedPromos) setPromoCodes(JSON.parse(savedPromos));
    if (savedSettings) setAdminSettings({
      ...defaultSettings,
      ...JSON.parse(savedSettings)
    });
  }, []);
  // Save to local storage on change
  useEffect(() => {
    localStorage.setItem('skybrand_products', JSON.stringify(products));
  }, [products]);
  useEffect(() => {
    localStorage.setItem('skybrand_cart', JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    localStorage.setItem('skybrand_orders', JSON.stringify(orders));
  }, [orders]);
  useEffect(() => {
    if (user) localStorage.setItem('skybrand_user', JSON.stringify(user));else localStorage.removeItem('skybrand_user');
  }, [user]);
  useEffect(() => {
    if (adminAuth) localStorage.setItem('skybrand_admin', JSON.stringify(adminAuth));else localStorage.removeItem('skybrand_admin');
  }, [adminAuth]);
  useEffect(() => {
    localStorage.setItem('skybrand_blocked_users', JSON.stringify(blockedUsers));
  }, [blockedUsers]);
  useEffect(() => {
    localStorage.setItem('skybrand_promos', JSON.stringify(promoCodes));
  }, [promoCodes]);
  useEffect(() => {
    localStorage.setItem('skybrand_settings', JSON.stringify(adminSettings));
  }, [adminSettings]);
  const addToCart = (product: Product, quantity = 1, selectedSize?: string, selectedColor?: string) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id && item.selectedSize === selectedSize && item.selectedColor === selectedColor);
      if (existing) {
        return prev.map(item => item.id === product.id && item.selectedSize === selectedSize && item.selectedColor === selectedColor ? {
          ...item,
          quantity: item.quantity + quantity
        } : item);
      }
      return [...prev, {
        ...product,
        quantity,
        selectedSize,
        selectedColor
      }];
    });
  };
  const removeFromCart = (productId: string) => setCart(prev => prev.filter(item => item.id !== productId));
  const updateCartQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev => prev.map(item => item.id === productId ? {
      ...item,
      quantity
    } : item));
  };
  const clearCart = () => setCart([]);
  const addOrder = (order: Order) => {
    setOrders(prev => [order, ...prev]);
    if (order.promoCode) {
      setPromoCodes(prev => prev.map(p => p.code === order.promoCode ? {
        ...p,
        usedCount: p.usedCount + 1
      } : p));
    }
  };
  const updateOrderStatus = (orderId: string, status: Order['status']) => {
    setOrders(prev => prev.map(order => order.id === orderId ? {
      ...order,
      status
    } : order));
  };
  const updateOrderDeliveryDate = (orderId: string, expectedDate: string, deliveryDate?: string) => {
    setOrders(prev => prev.map(order => order.id === orderId ? {
      ...order,
      expectedDeliveryDate: expectedDate,
      deliveryDate
    } : order));
  };
  const login = (userData: User) => setUser(userData);
  const logout = () => setUser(null);
  const updateUser = (userData: User) => setUser(userData);
  const addProduct = (product: Product) => setProducts(prev => [...prev, product]);
  const editProduct = (id: string, updatedProduct: Product) => setProducts(prev => prev.map(product => product.id === id ? updatedProduct : product));
  const deleteProduct = (id: string) => setProducts(prev => prev.filter(product => product.id !== id));
  const adminLogin = (username: string, password: string) => {
    if (username === 'admin' && password === 'skybrand2024') {
      setAdminAuth({
        isAuthenticated: true,
        username
      });
      return true;
    }
    return false;
  };
  const adminLogout = () => setAdminAuth(null);
  const blockUser = (mobile: string) => {
    if (!blockedUsers.includes(mobile)) setBlockedUsers(prev => [...prev, mobile]);
  };
  const activateUser = (mobile: string) => setBlockedUsers(prev => prev.filter(m => m !== mobile));
  const addPromoCode = (promo: PromoCode) => setPromoCodes(prev => [...prev, promo]);
  const editPromoCode = (id: string, updatedPromo: PromoCode) => setPromoCodes(prev => prev.map(p => p.id === id ? updatedPromo : p));
  const deletePromoCode = (id: string) => setPromoCodes(prev => prev.filter(p => p.id !== id));
  const validatePromoCode = (code: string, productIds: string[]) => {
    if (!adminSettings.promoSystemEnabled) return {
      valid: false,
      error: 'Promo system is currently disabled'
    };
    const promo = promoCodes.find(p => p.code.toUpperCase() === code.toUpperCase());
    if (!promo) return {
      valid: false,
      error: 'Invalid promo code'
    };
    if (!promo.isActive) return {
      valid: false,
      error: 'Promo code is inactive'
    };
    if (new Date(promo.expiryDate) < new Date()) return {
      valid: false,
      error: 'Promo code has expired'
    };
    if (promo.usageLimit > 0 && promo.usedCount >= promo.usageLimit) return {
      valid: false,
      error: 'Promo code usage limit reached'
    };
    if (promo.productIds.length > 0) {
      const hasEligibleProduct = productIds.some(id => promo.productIds.includes(id));
      if (!hasEligibleProduct) return {
        valid: false,
        error: 'Promo code not applicable for these products'
      };
    }
    return {
      valid: true,
      promo
    };
  };
  const updateAdminSettings = (settings: Partial<AdminSettings>) => {
    setAdminSettings(prev => ({
      ...prev,
      ...settings
    }));
  };
  return <AppContext.Provider value={{
    products,
    cart,
    orders,
    user,
    adminAuth,
    blockedUsers,
    promoCodes,
    adminSettings,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    addOrder,
    updateOrderStatus,
    updateOrderDeliveryDate,
    login,
    logout,
    updateUser,
    addProduct,
    editProduct,
    deleteProduct,
    adminLogin,
    adminLogout,
    blockUser,
    activateUser,
    addPromoCode,
    editPromoCode,
    deletePromoCode,
    validatePromoCode,
    updateAdminSettings
  }} data-id="element-105">
      {children}
    </AppContext.Provider>;
};
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) throw new Error('useAppContext must be used within an AppProvider');
  return context;
};